import { ParticipationStatusType } from './attendance';
import { AvailabilityType } from './availability';
import { ContactType } from './contact';
import { CourseTypeEnum } from './course';
import { DunningLevel, InvoicePaymentStatus } from './invoice';
import {
  MembershipRequestStatus,
  MembershipRequestType,
} from './membershipRequest';
import { NoteStatusEnum, NoteTypeEnum } from './note';
import { PaymentInterval, PaymentType } from './paymentMandateData';
import { TypeOfPeriod2 } from './periodTypes';
import { CostTypeEnum } from './reference';
import { TrainerPaymentTypeEnum } from './trainerPayment';

export enum LogEntityType {
  Attendee = 'Attendee',
  Course = 'Course',
  Appointment = 'Appointment',
  Employee = 'Employee',
}

export interface LogEntry {
  id: string;
  dateTime: string;
  executedByName: string;
  parsedData:
    | LogEntryParsedDataAttendee
    | LogEntryParsedDataCourse
    | LogEntryParsedDataAppointment
    | LogEntryParsedDataEmployee
    | any;
  displayText?: string[][];
}

export interface LogEntryParsedDataAttendee {
  UserName?: string;
  Email?: string;
  FromDate?: string;
  TillDate?: string;
  Active?: boolean;
  Unlocked?: boolean;
  Salutation?: string;
  FirstName?: string;
  LastName?: string;
  Phone?: string;
  Birthday?: string;
  Title?: string;
  Zip?: string;
  Street?: string;
  City?: string;
  Text?: string;
  LoginDate?: string;
  MemberNumber?: string;
  MembershipText?: string;
  MainLocationName?: string;
  MemberStatusName?: string;
  AddedLocationNames?: string[];
  RemovedLocationNames?: string[];
  AddedDepartmentNames?: string[];
  RemovedDepartmentNames?: string[];
  AddedCharacteristicNames?: string[];
  RemovedCharacteristicNames?: string[];
  AddedAttendeeRoleNames?: string[];
  RemovedAttendeeRoleNames?: string[];
  AddedMembershipTypes?: {
    IdNumber: string;
    MembershipTypeName: string;
    Text?: string | null;
    FromDate?: string | null;
    TillDate?: string | null;
  }[];
  RemovedMembershipTypes?: {
    IdNumber: string;
    MembershipTypeName: string;
    Text?: string | null;
    FromDate?: string | null;
    TillDate?: string | null;
  }[];
  UpdatedMembershipTypes?: {
    IdNumber: string;
    MembershipTypeName: string;
    Text?: string | null;
    FromDate?: string | null;
    TillDate?: string | null;
  }[];
  UpdatedPaymentMandate?: {
    Type?: PaymentType;
    Interval?: PaymentInterval;
    IBAN?: string | null;
    AccountHolder?: string | null;
    MandateReference?: string | null;
    MandateDate?: string | null;
    MandateValidityTillDate?: string | null;
  };
  AddedInvoices?: {
    InvoiceId: string;
    InvoiceNumber: number;
    PaymentStatus: InvoicePaymentStatus;
  }[];
  UpdatedInvoices?: {
    InvoiceId: string;
    InvoiceNumber: number;
    PaymentStatus?: InvoicePaymentStatus;
    DunningLevel?: DunningLevel;
    PartiallyPaidCentAmount?: number | null;
    DueDate?: string | null;
    Deleted?: boolean;
  }[];
  AddedInvoicePositions?: {
    InvoiceNumber: number;
    Description: string;
    CentCost: number;
    Note: string | null;
    CustomCostInfo: string | null;
    ForAttendeeName?: string | null;
    Department?: { departmentId: string; departmentName: string } | null;
  }[];
  UpdatedInvoicePositions?: {
    InvoiceNumber: number;
    Description: string;
    Cancelled?: boolean;
    CentCost?: number;
    UpdatedDescription?: string;
    Note?: string | null;
    CustomCostInfo?: string | null;
    Department?: { departmentId: string; departmentName: string } | null;
  }[];
  AddedAttendances?: {
    AttendanceId: string;
    CourseName: string | null;
    Date: string | null;
    TimeFrom: string | null;
    TimeTill: string | null;
    Status: ParticipationStatusType;
    WaitingListPosition: number;
  }[];
  UpdatedAttendances?: {
    AttendanceId: string;
    CourseName: string | null;
    Date: string | null;
    TimeFrom: string | null;
    TimeTill: string | null;
    Status?: ParticipationStatusType;
    Note?: string | null;
    WaitingListPosition?: number;
  }[];
  AddedAttendeeCourses?: {
    DateTimeId: string;
    CourseName: string | null;
    DateFrom: string | null;
    DateTill: string | null;
    OnWaitingList: boolean;
    WaitingListPosition: number;
  }[];
  UpdatedAttendeeCourses?: {
    DateTimeId: string;
    CourseName: string | null;
    DateFrom: string | null;
    DateTill: string | null;
    OnWaitingList?: boolean;
    WaitingListPosition?: number;
    Cancelled?: boolean;
    DeletedFromWaitingList?: boolean;
  }[];
  AddedDocuments?: {
    DocumentId: string;
    Title: string | null;
    Text: string | null;
    FileName: string | null;
    Category: string | null;
    VisibleForUser: boolean;
  }[];
  UpdatedDocuments?: {
    DocumentId: string;
    Title: string | null;
    UpdatedTitle?: string | null;
    Text?: string | null;
    FileName?: string | null;
    Category?: string | null;
    VisibleForUser?: boolean;
  }[];
  RemovedDocuments?: {
    DocumentId: string;
    Title: string | null;
  }[];
  AddedNotes?: {
    NoteId: string;
    Name: string | null;
    Text: string | null;
    Type: NoteTypeEnum;
    Status: number;
  }[];
  UpdatedNotes?: {
    NoteId: string;
    Name: string | null;
    UpdatedName?: string | null;
    Text?: string | null;
    Type?: NoteTypeEnum;
    Status?: NoteStatusEnum;
  }[];
  RemovedNotes?: {
    NoteId: string;
    Name: string | null;
  }[];
  AttendeeAddedAsSubAttendee?: {
    FullName: string | null;
    MemberNumber: string | null;
  };
  InterestedChangedToMainAttendee?: {
    UserName: string | null;
    MemberNumber: string | null;
  };
  InterestedChangedToSubAttendee?: {
    FullName: string | null;
    MemberNumber: string | null;
  };
  SubAttendeeChangedToMainAttendee?: {
    UserName: string | null;
    MemberNumber: string | null;
  };
  SubAttendeeRemovedFromApplicationUser?: {
    FullName: string | null;
    MemberNumber: string | null;
  };
  SubAttendeeAddedToApplicationUser?: {
    FullName: string | null;
    MemberNumber: string | null;
  };
  AddedContacts?: {
    contactId: string;
    contactType: ContactType;
    lastName: string | null;
    firstName: string | null;
    email: string | null;
    phone: string | null;
    memberNumber: string | null;
  }[];
  UpdatedContacts?: {
    contactId: string;
    contactType: ContactType;
    lastName: string | null;
    firstName: string | null;
    email: string | null;
    phone: string | null;
    memberNumber: string | null;
  }[];
  RemovedContacts?: {
    contactId: string;
    contactType: ContactType;
    lastName: string | null;
    firstName: string | null;
    email: string | null;
    phone: string | null;
    memberNumber: string | null;
  }[];
  PasswordResetEmailSentTo?: string;
  AddedSubAttendees?: {
    FullName: string;
    MemberNumber: string | null;
  }[];
  RemovedSubAttendees?: {
    FullName: string;
    MemberNumber: string | null;
  }[];
  UpdatedFixedCosts?: {
    CostName: string;
    CostType: CostTypeEnum;
    Enabled: boolean;
  }[];
  UploadedLogoFile?: string;
  DeletedLogoFile?: string;
  EnteredPresence?: {
    LocationName: string | null;
    DateEnter: string;
    Text: string | null;
  };
  LeftPresence?: {
    LocationName: string | null;
    DateLeave: string;
    Text: string | null;
  };
  AddedRoomPresence?: {
    LocationName: string;
    RoomName: string;
    Date: string;
    TimeEnter: string;
    TimeLeave: string | null;
    Status: ParticipationStatusType;
  };
  UpdatedRoomPresence?: {
    LocationName: string;
    RoomName: string;
    PreviousRoomName?: string;
    TimeEnter?: string;
    TimeLeave?: string | null;
    Status?: ParticipationStatusType;
  };
  AddedMembershipRequests?: {
    MembershipRequestId: string;
    Type: MembershipRequestType;
    Status: MembershipRequestStatus;
    MembershipType?: {
      MembershipTypeName: string;
    };
    StartDate: string | null;
    EndDate: string | null;
    Reason: string | null;
    Wishes: string | null;
    AdditionalData: string | null;
  }[];
  UpdatedMembershipRequests?: {
    MembershipRequestId: string;
    Type: MembershipRequestType;
    Status?: MembershipRequestStatus;
    MembershipType?: {
      MembershipTypeName: string;
    };
    EditingUserName?: string | null;
    EditedByName?: string;
  }[];
  RemovedMembershipRequests?: {
    MembershipRequestId: string;
    Type: MembershipRequestType;
    MembershipType?: {
      MembershipTypeName: string;
    };
  }[];
}

export interface LogEntryParsedDataCourse {
  Active?: boolean;
  BookableAsSeries?: boolean;
  Color?: string;
  Description?: string;
  MaxAttendees?: number;
  Name?: string;
  Type?: CourseTypeEnum;
  TariffName?: string;
  ResponsibleName?: string;
  AddedLocationNames?: string[];
  RemovedLocationNames?: string[];
  AddedDepartmentNames?: string[];
  RemovedDepartmentNames?: string[];
  AddedSkillNames?: string[];
  RemovedSkillNames?: string[];
  AddedEquipmentNames?: string[];
  RemovedEquipmentNames?: string[];
  AddedDateTime?: {
    Name: string;
    LocationName: string | null;
    RoomName: string | null;
    TariffName: string | null;
    DateFrom: string;
    DateTill: string | null;
    TimeFrom: string;
    TimeTill: string;
    Period: TypeOfPeriod2;
    WeeklyPeriodValues?: string;
    MonthlyPeriodValues?: string;
    EachOnDay: number;
    MaxAttendees: number;
  };
  UpdatedDateTime?: {
    Name: string;
    NewName?: string;
    LocationName?: string | null;
    RoomName?: string | null;
    TariffName?: string | null;
    DateFrom?: string;
    DateTill?: string | null;
    TimeFrom?: string;
    TimeTill?: string;
    Period?: TypeOfPeriod2;
    WeeklyPeriodValues?: string;
    MonthlyPeriodValues?: string;
    EachOnDay?: number;
    MaxAttendees?: number;
  };
  CourseTrainers?: {
    DateTimeName: string;
    AddedTrainerNames?: string[];
    RemovedTrainerNames?: string[];
    AddedSubstituteTrainerNames?: string[];
    RemovedSubstituteTrainerNames?: string[];
  };
  DateTimeSkills?: {
    DateTimeName: string;
    AddedSkillNames?: string[];
    RemovedSkillNames?: string[];
  };
  DeletedDateTime?: {
    Name: string;
  };
  AddedAppointment?: {
    RoomName: string | null;
    Date: string;
    TimeFrom: string;
    TimeTill: string;
    MaxAttendees: number;
    Text: string;
  };
  UpdatedAppointment?: {
    OriginalDate: string;
    OriginalTimeFrom: string;
    OriginalTimeTill: string;
    RoomName: string | null;
    Date: string;
    TimeFrom: string;
    TimeTill: string;
    MaxAttendees: number;
    Text: string;
  };
  AppointmentTrainers?: {
    AppointmentDate: string;
    AppointmentTimeFrom: string;
    AppointmentTimeTill: string;
    TrainerNames?: string[];
    SubstituteTrainerNames?: string[];
    OtherTrainerNames?: string[];
  };
  DeletedAppointment?: {
    DateTimeName: string;
    Date: string;
    TimeFrom: string;
    TimeTill: string;
  };
  CancelledAppointment?: {
    Date: string;
    TimeFrom: string;
    TimeTill: string;
  };
  AddedDocuments?: {
    DocumentId: string;
    Title: string | null;
    Text: string | null;
    FileName: string | null;
    Category: string | null;
  }[];
  UpdatedDocuments?: {
    DocumentId: string;
    Title: string | null;
    UpdatedTitle?: string | null;
    Text?: string | null;
    FileName?: string | null;
    Category?: string | null;
  }[];
  RemovedDocuments?: {
    DocumentId: string;
    Title: string | null;
  }[];
  AddedNotes?: {
    NoteId: string;
    Name: string | null;
    Text: string | null;
  }[];
  UpdatedNotes?: {
    NoteId: string;
    Name: string | null;
    UpdatedName?: string | null;
    Text?: string | null;
  }[];
  RemovedNotes?: {
    NoteId: string;
    Name: string | null;
  }[];
  UploadedLogoFile?: string;
  DeletedLogoFile?: string;
}

export interface LogEntryParsedDataAppointment {
  ManualChange?: {
    RoomName: string | null;
    Date: string;
    TimeFrom: string;
    TimeTill: string;
    MaxAttendees: number;
    Text: string;
  };
  Trainers?: {
    TrainerNames: string[];
    SubstituteTrainerNames: string[];
    OtherTrainerNames: string[];
  };
  AddedAttendances?: {
    AttendeeName?: string;
    TrainerName?: string;
    Status: ParticipationStatusType;
    Note: string | null;
    WaitingListPosition: number;
  }[];
  UpdatedAttendances?: {
    AttendeeName?: string;
    TrainerName?: string;
    Status?: ParticipationStatusType;
    Note?: string | null;
    WaitingListPosition?: number;
  }[];
  DeletedAttendances?: {
    AttendeeName?: string;
    TrainerName?: string;
  }[];
  Cancelled?: true;
}

export interface LogEntryParsedDataEmployee {
  UserName?: string;
  Email?: string;
  FirstName?: string;
  LastName?: string;
  Salutation?: string;
  Title?: string | null;
  Phone?: string | null;
  Text?: string | null;
  EmployeeNo?: string | null;
  IsIntern?: boolean;
  FromDate?: string;
  TillDate?: string | null;
  Birthday?: string | null;
  Active?: boolean;
  Unlocked?: boolean;
  ResponsibleName?: string | null;
  AddedLocationNames?: string[];
  RemovedLocationNames?: string[];
  AddedDepartmentNames?: string[];
  RemovedDepartmentNames?: string[];
  AddedRoleNames?: string[];
  RemovedRoleNames?: string[];
  Permissions?: {
    HomeConflictsViewEdit?: boolean;
    HomeTodoViewEdit?: boolean;
    HomeNewsView?: boolean;
    HomeNewsCreateDelete?: boolean;
    HomeNewsEdit?: boolean;
    HomeReportsView?: boolean;
    HomeCalendarEdit?: boolean;
    HomeCalendarChatUse?: boolean;
    LocationsView?: boolean;
    LocationsCreateDelete?: boolean;
    LocationsEdit?: boolean;
    EmployeesView?: boolean;
    EmployeesCreateDelete?: boolean;
    EmployeesEdit?: boolean;
    TrainersView?: boolean;
    TrainersCreateDelete?: boolean;
    TrainersEdit?: boolean;
    CoursesView?: boolean;
    CoursesCreateDelete?: boolean;
    CoursesEdit?: boolean;
    DateTimesView?: boolean;
    DateTimesEdit?: boolean;
    AppointmentsView?: boolean;
    AppointmentsCreateDelete?: boolean;
    AppointmentsEdit?: boolean;
    InvoicesView?: boolean;
    InvoicesEdit?: boolean;
    InvoicePositionsView?: boolean;
    InvoicePositionsEdit?: boolean;
    InvoiceRunsView?: boolean;
    InvoiceRunsCreateDelete?: boolean;
    TrainerPayrollsView?: boolean;
    TrainerPayrollsEdit?: boolean;
    DirectDebitViewCreate?: boolean;
    AttendeesView?: boolean;
    AttendeesCreateDelete?: boolean;
    AttendeesEdit?: boolean;
    InterestedView?: boolean;
    InterestedCreateDelete?: boolean;
    InterestedEdit?: boolean;
    StatisticsViewCreate?: boolean;
    MyCompanyViewEdit?: boolean;
    CompanySubscriptionsViewEdit?: boolean;
    SettingsViewEdit?: boolean;
    ReferencesView?: boolean;
    TariffsCreateDelete?: boolean;
    TariffsEdit?: boolean;
    DepartmentsCreateDelete?: boolean;
    DepartmentsEdit?: boolean;
    MembershipTypesCreateDelete?: boolean;
    MembershipTypesEdit?: boolean;
    MemberStatusesCreateDelete?: boolean;
    MemberStatusesEdit?: boolean;
    CostsCreateDelete?: boolean;
    CostsEdit?: boolean;
    LicencesCreateDelete?: boolean;
    LicencesEdit?: boolean;
    SkillsCreateDelete?: boolean;
    SkillsEdit?: boolean;
    CharacteristicsCreateDelete?: boolean;
    CharacteristicsEdit?: boolean;
    EquipmentsCreateDelete?: boolean;
    EquipmentsEdit?: boolean;
    AttendeeRolesEdit?: boolean;
    AttendeeRolesCreateDelete?: boolean;
  };
  PasswordResetEmailSentTo?: string;
  UploadedLogoFile?: string;
  DeletedLogoFile?: string;
  EnteredPresence?: {
    LocationName: string | null;
    DateEnter: string;
    Text: string | null;
  };
  LeftPresence?: {
    LocationName: string | null;
    DateLeave: string;
    Text: string | null;
  };
  LoginDate?: string;
  AddedAttendances?: {
    AttendanceId: string;
    CourseName: string | null;
    Date: string | null;
    TimeFrom: string | null;
    TimeTill: string | null;
    Status: ParticipationStatusType;
  }[];
  UpdatedAttendances?: {
    AttendanceId: string;
    CourseName: string | null;
    Date: string | null;
    TimeFrom: string | null;
    TimeTill: string | null;
    Status?: ParticipationStatusType;
    Note?: string | null;
  }[];
  AddedDocuments?: {
    DocumentId: string;
    Title: string | null;
    Text: string | null;
    FileName: string | null;
    Category: string | null;
  }[];
  UpdatedDocuments?: {
    DocumentId: string;
    Title: string | null;
    UpdatedTitle?: string | null;
    Text?: string | null;
    FileName?: string | null;
    Category?: string | null;
  }[];
  RemovedDocuments?: {
    DocumentId: string;
    Title: string | null;
  }[];
  AddedNotes?: {
    NoteId: string;
    Name: string | null;
    Text: string | null;
  }[];
  UpdatedNotes?: {
    NoteId: string;
    Name: string | null;
    UpdatedName?: string | null;
    Text?: string | null;
  }[];
  RemovedNotes?: {
    NoteId: string;
    Name: string | null;
  }[];
  AddedSkills?: {
    SkillName: string;
    Level: string | null;
    Text: string | null;
  }[];
  UpdatedSkills?: {
    SkillName: string;
    Level?: string | null;
    Text?: string | null;
  }[];
  RemovedSkills?: {
    SkillName: string;
  }[];
  AddedAvailabilities?: {
    AvailabilityId: string;
    Type: AvailabilityType;
    Name: string;
    Text: string | null;
    AllDay: boolean;
    TimeFrom: string | null;
    TimeTill: string | null;
    DateFrom: string | null;
    DateTill: string | null;
    Period: TypeOfPeriod2;
    PeriodValues?: string | null;
  }[];
  UpdatedAvailabilities?: {
    AvailabilityId: string;
    Name: string;
    UpdatedName?: string;
    Type?: AvailabilityType;
    Text?: string | null;
    AllDay?: boolean;
    TimeFrom?: string | null;
    TimeTill?: string | null;
    DateFrom?: string | null;
    DateTill?: string | null;
    Period?: TypeOfPeriod2;
    PeriodValues?: string | null;
  }[];
  RemovedAvailabilities?: {
    AvailabilityId: string;
    Name: string;
  }[];
  TrainerAppointmentAssignment?: {
    CourseName: string;
    AppointmentDate: string;
    AppointmentTimeFrom: string;
    AppointmentTimeTill: string;
  };
  TrainerAppointmentUnAssignment?: {
    CourseName: string;
    AppointmentDate: string;
    AppointmentTimeFrom: string;
    AppointmentTimeTill: string;
  };
  TrainerDateTimeAssignment?: {
    CourseName: string;
    DateTimeName: string;
    DateTimeDateFrom: string;
    DateTimeDateTill: string | null;
    DateTimeTimeFrom: string;
    DateTimeTimeTill: string;
    AsSubstitute: boolean;
  };
  TrainerDateTimeUnAssignment?: {
    CourseName: string;
    DateTimeName: string;
    DateTimeDateFrom: string;
    DateTimeDateTill: string | null;
    DateTimeTimeFrom: string;
    DateTimeTimeTill: string;
    AsSubstitute: boolean;
  };
  AddedLicences?: {
    LicenceName: string;
    Text: string | null;
  }[];
  UpdatedLicences?: {
    LicenceName: string;
    Text: string | null;
  }[];
  RemovedLicences?: {
    LicenceName: string;
  }[];
  IBAN?: string | null;
  AccountHolder?: string | null;
  AddedPayments?: {
    Name: string;
    Type: TrainerPaymentTypeEnum;
    DateFrom: string;
    DateTill: string;
    Fee: number;
    Hours: number;
    CostCenter: string;
    Department: { DepartmentId: string; DepartmentName: string } | null;
  }[];
  UpdatedPayments?: {
    Name: string;
    UpdatedName?: string;
    Type?: TrainerPaymentTypeEnum;
    DateFrom?: string;
    DateTill?: string;
    Fee?: number;
    Hours?: number;
    CostCenter?: string;
    Department?: { DepartmentId: string; DepartmentName: string } | null;
  }[];
  RemovedPayments?: {
    Name: string;
  }[];
  AddedTrainerHours?: {
    Date: string;
    TimeFrom: string;
    TimeTill: string;
    Course: { CourseId: string; CourseName: string } | null;
    Payment: { PaymentId: string; PaymentName: string } | null;
    FeeTotal: number;
    CostCenter: string;
    Description: string | null;
  }[];
  UpdatedTrainerHours?: {
    Date: string;
    TimeFrom: string;
    TimeTill: string;
    UpdatedDate?: string;
    UpdatedTimeFrom?: string;
    UpdatedTimeTill?: string;
    Course?: { CourseId: string; CourseName: string } | null;
    Payment?: { PaymentId: string; PaymentName: string } | null;
    FeeTotal?: number;
    CostCenter?: string;
    Description?: string | null;
  }[];
  RemovedTrainerHours?: {
    Date: string;
    TimeFrom: string;
    TimeTill: string;
  }[];
}

function parse(data?: Partial<LogEntry>): LogEntry {
  return {
    id: data?.id || '',
    dateTime: data?.dateTime || new Date().toISOString(),
    executedByName: data?.executedByName || '',
    parsedData: data?.parsedData || {},
  };
}

export default {
  parse,
};
